import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import checkoutApi from 'api/requests/checkout';
import establishmentsApi from 'api/requests/establishments';
import { FullyCentered } from 'styled/alignment/Center';
import Loader from 'components/_UI/Loader';
import Text from 'components/_UI/Text';
import Button from 'components/_UI/Button';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import indexPriority from 'theme/z-index';
import { Row } from 'styled/alignment/Row';
import { useCheckout } from 'context/checkout/Checkout';
import TicketItem from './TicketItem';
import breakpoints from 'theme/breakpoints';
import PaymentForm from '../payment/Payment';
import FreePaymentForm from '../payment/FreePayment';
import ProductItem from './ProductItem';
import buttonText from 'texts/buttons/buttonText';
import currency from 'helpers/currency';
import serviceFee from 'helpers/serviceFee';
import { formatSessionWhen } from 'helpers/eventSessions';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import Modal from 'components/_UI/Modal';
import CheckoutCollapse from 'modules/events/page/collapse/CheckoutCollapse';
import routeParams from 'theme/routeParams';
import auth from 'middlewares/auth';
import LoginModal from 'modules/onboarding/sign-in/LoginModal';
import paths from 'routes/paths';
import { PRODUCT_TYPES } from '../commom/paymentFormUtils';
import { resolveOrderError } from '../commom/orderErrors';
import { reservationEndsAt } from '../commom/reservation';
import { cartDetails, trackEvent } from 'scripts/pixels';
import eventAnalyticsApi from 'api/requests/eventAnalytics';
import { getAnalyticsSessionId } from 'scripts/analyticsSession';
import { getAttributionPayload } from 'scripts/attribution';
import { Check, ShoppingCart, Sparkles, Tag, X } from 'lucide-react';
import Fieldset from 'components/_UI/Fieldset';
import promotionalCodesApi from 'api/requests/promotionalCodes';
import ServiceFeeInfo from 'components/_UI/ServiceFeeInfo';
export default function CheckoutOrderMinimal({ event, establishment, schedule }) {
    const location = useLocation();
    const navigate = useNavigate();

    const { checkout, setCheckout } = useCheckout();
    const [searchParams, setSearchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [originalPriceTotal, setOriginalPriceTotal] = useState(0)
    const [serviceFeeTotal, setServiceFeeTotal] = useState(0)
    // Acumulado em paralelo ao de cartão: a taxa do evento pode ser diferente
    // por meio de pagamento, e o meio só é escolhido na etapa seguinte.
    const [serviceFeePixTotal, setServiceFeePixTotal] = useState(0)
    const [error, setError] = useState(false)
    const [getTicketsError, setGetTicketsError] = useState('')
    const [isCouponModalVisible, setIsCouponModalVisible] = useState(false)
    const [couponCode, setCouponCode] = useState('')
    const [couponId, setCouponId] = useState('')
    const [cartItems, setCartItems] = useState([])
    const [couponTrigger, setCouponTrigger] = useState(0)
    const [errorMinOrder, setErrorMinOrder] = useState(false)
    const [createOrderError, setCreateOrderError] = useState('')
    const [componentToRender, setComponentToRender] = useState(false)
    const [goToLoginModal, setGoToLoginModal] = useState(false)
    const [loginModalCount, setLoginModalCount] = useState(0);
    const [isPix, setIsPix] = useState(false);
    const [pixToRender, setPixToRender] = useState(false);
    const [fullscreenImage, setFullscreenImage] = useState(null);
    const [activeOverlayProductId, setActiveOverlayProductId] = useState(null);
    const [promotionalCode, setPromotionalCode] = useState()
    const [typeDiscount, setTypeDiscount] = useState()
    const [errorCode, setErrorCode] = useState(false)

    // Há taxa de serviço a exibir sempre que algum ingresso/produto disponível a repassa ao cliente.
    // Um lote pode ter taxa zero no cartão e não no pix (ou o contrário), então
    // vale qualquer um dos dois meios.
    // O resumo mostra a taxa de cartão, porque o meio de pagamento só é
    // escolhido na etapa seguinte. Quando a taxa negociada do pix é menor, a
    // economia é dita aqui — antes de o cliente sequer chegar na escolha.
    const pixSavings = serviceFeeTotal - serviceFeePixTotal > 0.001
        ? serviceFeeTotal - serviceFeePixTotal
        : 0;

    // Mesma comparação de pixSavings, usada no resumo com cupom aplicado
    // para decidir se mostra a nota de taxa diferente no PIX.
    const pixFeeDiffers = Math.abs(serviceFeeTotal - serviceFeePixTotal) > 0.001;

    const hasServiceFee = [...tickets, ...products].some(
        (item) => !item.absorbed_service_fee && (item.batches || []).some(batch => (
            serviceFee.perUnit(batch, serviceFee.METHODS.CREDIT_CARD) > 0
            || serviceFee.perUnit(batch, serviceFee.METHODS.PIX) > 0
        ))
    );

    /*
     * Layout de programação: quem escolhe os ingressos é a programação (um por
     * tipo), e esta tela vira o carrinho. O resto do fluxo — cupom, total,
     * pedido e pagamento — é o mesmo, então os mesmos estados são preenchidos
     * a partir da escolha, em vez de cada ingresso controlar a sua quantidade.
     */
    const scheduleItems = useMemo(() => {
        if (!schedule) {
            return [];
        }

        return schedule.selected
            .map(productId => {
                const product = (tickets || []).find(item => item.id === productId);
                const batch = product?.batches?.find(item => item.is_active && item.quantity > 0);

                return product && batch ? { product, batch } : null;
            })
            .filter(Boolean);
    }, [schedule?.selected, tickets]);

    useEffect(() => {
        if (!schedule) {
            return;
        }

        const totals = scheduleItems.reduce((sum, { product, batch }) => ({
            price: sum.price + Number(batch.price),
            original: sum.original + Number(batch.original_price ?? batch.price),
            fee: sum.fee + serviceFee.perUnit(batch, serviceFee.METHODS.CREDIT_CARD, product.absorbed_service_fee),
            pixFee: sum.pixFee + serviceFee.perUnit(batch, serviceFee.METHODS.PIX, product.absorbed_service_fee),
        }), { price: 0, original: 0, fee: 0, pixFee: 0 });

        setTotalPrice(totals.price);
        setOriginalPriceTotal(totals.original);
        setServiceFeeTotal(totals.fee);
        setServiceFeePixTotal(totals.pixFee);

        setCartItems(scheduleItems.map(({ product, batch }) => ({
            ...batch,
            type: product.type,
            absorbed_service_fee: product.absorbed_service_fee,
            requires_document: product.requires_document,
        })));

        // Cupom, erro de estoque e fim de pagamento zeram o `checkout`; como esta
        // sincronização roda a cada mudança dele, o carrinho se refaz sozinho.
        const batches = scheduleItems.map(({ batch }) => ({ id: batch.id, quantity: 1 }));

        if (JSON.stringify(checkout?.batches || []) !== JSON.stringify(batches)) {
            setCheckout(prev => ({ ...prev, batches }));
        }
    }, [schedule?.selected, scheduleItems, checkout]);

    useEffect(() => {
        const couponParam = searchParams.get('coupon');

        if (couponParam) {
            setCouponCode(couponParam);
            getOrder(couponParam)
            return
        }

        setTotalPrice(0);
        setOriginalPriceTotal(0);
        setServiceFeeTotal(0);
        setServiceFeePixTotal(0);
        setCouponCode('');
        setCheckout({});
        setCartItems([]);
        getOrder();
    }, [event]);


    const handleOpenImage = (imgUrl) => {
        setFullscreenImage(imgUrl);
    };

    const handleCloseImage = () => {
        setFullscreenImage(null);
    };


    async function getOrder(coupon) {
        setLoading(true);
        const hiddenCode = searchParams.get(routeParams.payments.hiddenCode)

        try {
            if (establishment) {

                const { data } = await establishmentsApi.getProducts(establishment.slug)
                if (data && data.data) {

                    setProducts(
                        data.data
                            .filter(product => product?.batches?.[0]?.quantity_available > 0 && Boolean(product?.batches?.[0]?.is_active))
                            .map(product => {
                                const batches = product.batches.map(batch => {
                                    return {
                                        ...batch,
                                        original_price: batch?.original_price || batch.price
                                    }
                                })

                                return {
                                    ...product,
                                    batches
                                }
                            })
                    )
                }
            }

            if (event && !establishment && coupon !== 'remove') {

                const { data } = await checkoutApi.getTickets(event?.slug, coupon ? { params: { coupon, hidden_code: hiddenCode } } : { params: { hidden_code: hiddenCode } });

                setTickets(data?.items?.filter(el => el.type === PRODUCT_TYPES.TICKET));
                setProducts(data?.items?.filter(el => el.type === PRODUCT_TYPES.PRODUCT));
                setCouponId(data?.coupon_id);

                if (coupon) {
                    setCouponCode(data?.coupon_id ? coupon : null);
                    setGetTicketsError('')

                    setTotalPrice(0)
                    setOriginalPriceTotal(0)
                    setServiceFeeTotal(0)
                    setServiceFeePixTotal(0)
                    setCheckout({})
                }
            }


            if (coupon == 'remove') {
                const { data } = await checkoutApi.getTickets(event?.slug, { hidden_code: hiddenCode });


                setTickets(data?.items?.filter(el => el.type === PRODUCT_TYPES.TICKET));
                setProducts(data?.items?.filter(el => el.type === PRODUCT_TYPES.PRODUCT));
                setCouponId(null);

                if (coupon) {

                    setCouponCode(null);
                    setGetTicketsError('')

                    setTotalPrice(0)
                    setOriginalPriceTotal(0)
                    setServiceFeeTotal(0)
                    setServiceFeePixTotal(0)
                    setCheckout({})
                }
            }



        } catch (error) {
            setGetTicketsError('Código promocional inválido.');
        } finally {
            setLoading(false);
        }
    }

    const getCoupon = async (code, slug) => {
        setLoading(true)
        if (errorCode) {
            setErrorCode(false)
        }
        try {
            const data = await promotionalCodesApi.getCoupon(slug, code)
            setTypeDiscount(data.data.data.total_discount)
            getOrder(code)
        } catch (error) {
            setLoading(false)
            setErrorCode(true)
        }
    }


    const handleUpdateTotal = (priceChange, originalPriceChange, feeChange = 0, pixFeeChange = 0) => {
        if (!auth.getToken()) {
            setLoginModalCount(loginModalCount + 1)

            if (loginModalCount > 0) {
                setGoToLoginModal(true);
                return
            }
        }

        setTotalPrice((prevTotal) => (parseFloat(prevTotal) + priceChange));
        setOriginalPriceTotal((prevTotal) => (parseFloat(prevTotal) + originalPriceChange));
        setServiceFeeTotal((prevFee) => Math.max(0, parseFloat(prevFee) + feeChange));
        setServiceFeePixTotal((prevFee) => Math.max(0, parseFloat(prevFee) + pixFeeChange));
    }

    async function buy() {
        if (!auth.getToken()) {
            setGoToLoginModal(true);
            return;
        }

        const payload = {
            ...checkout
        }

        if (event) {
            payload.event_id = event.id
        }

        // Layout de programação: a presença nas sessões escolhidas vai no mesmo pedido do ingresso.
        if (schedule?.presences?.length) {
            payload.session_ids = schedule.presences.map(session => session.id)
        }

        if (establishment) {
            payload.establishment_id = establishment.id
        }

        if (couponId) {
            payload.coupon_id = couponId
        }

        // A campanha que trouxe o comprador viaja com o pedido: é o que permite
        // o relatório do painel e a Conversions API creditarem a venda depois.
        const attribution = getAttributionPayload()

        if (attribution) {
            payload.attribution = attribution
        }

        // O meio de pagamento ainda não foi escolhido aqui, e cartão e pix podem
        // ter taxas diferentes: vale o menor total, senão passaria um pedido que
        // o backend recusa depois por valor mínimo.
        const lowestTotal = totalPrice + Math.min(serviceFeeTotal, serviceFeePixTotal);

        if (lowestTotal < 5 && totalPrice !== 0) {
            setErrorMinOrder('Valor mínimo do pedido é de 5 reais!')
            return
        }

        if (!payload?.batches?.length) {
            setError(true)
            return
        }

        setLoading(true);

        try {
            trackEvent('InitiateCheckout', {
                value: lowestTotal,
                ...cartDetails(payload.batches),
            });
            if (event?.id) {
                eventAnalyticsApi.track(event.id, 'checkout_click', getAnalyticsSessionId()).catch(() => {});
            }
            const { data } = await checkoutApi.createOrder(payload);

            const snapshotTotalPrice = totalPrice;
            const snapshotFeeTotal = serviceFeeTotal;
            const snapshotPixFeeTotal = serviceFeePixTotal;

            setTotalPrice(0);
            setOriginalPriceTotal(0);
            setServiceFeeTotal(0);
            setServiceFeePixTotal(0);

            setCheckout({ ...checkout, orderId: data.id, reservationEndsAt: reservationEndsAt(data) });
            setSearchParams({ 'checkout': 1 })

            if (Number(snapshotTotalPrice) === 0) {
                setComponentToRender(() => (
                    <StyledCheckoutOrder>
                        <FreePaymentForm
                            tickets={cartItems}
                            event={event}
                            onExpire={onCloseCollapse}
                        />
                    </StyledCheckoutOrder>
                ))
            } else {
                setComponentToRender(() => (
                    <StyledCheckoutOrder>
                        <PaymentForm
                            tickets={cartItems}
                            totalPrice={snapshotTotalPrice + snapshotFeeTotal}
                            totalPriceByMethod={{
                                creditcard: snapshotTotalPrice + snapshotFeeTotal,
                                pix: snapshotTotalPrice + snapshotPixFeeTotal,
                            }}
                            onPixTrigger={(pixComponent) => renderPix(pixComponent)}
                            onPaymentSuccess={onCloseCollapse}
                        />
                    </StyledCheckoutOrder>
                ))
            }

        } catch ({ response }) {
            if (response?.status === 401) {
                setGoToLoginModal(true);
                return;
            }

            handleOrderError(response)
        } finally {
            setLoading(false);
        }
    }

    function removeCode() {
        getOrder('remove')
    }

    function renderPix(PixComponent) {
        setIsPix(true);
        setPixToRender(() => (<PixComponent />))
    }

    function toggleCouponModal() {
        if (!auth.getToken()) {
            setLoginModalCount(loginModalCount + 1)

            if (loginModalCount > 0) {
                setGoToLoginModal(true);
                return
            }
        }

        setIsCouponModalVisible(!isCouponModalVisible)
        setGetTicketsError('');
    }

    /**
     * Toda recusa do pedido precisa virar texto na tela. Quando o motivo é
     * estoque velho, o carrinho também é relido — sem isso o comprador continua
     * olhando um número que não existe mais e tenta de novo.
     */
    function handleOrderError(response) {
        const { message, shouldRefreshStock } = resolveOrderError(response)

        if (!shouldRefreshStock) {
            setCreateOrderError(message)
            return
        }

        setCreateOrderError(`${message} Atualizando os itens disponíveis.`)

        setTimeout(() => {
            setCreateOrderError('')
            setCheckout({})
            getOrder()
        }, 2500)
    }

    function onCloseCollapse() {
        setTotalPrice(0);
        setOriginalPriceTotal(0);
        setServiceFeeTotal(0);
        setServiceFeePixTotal(0);
        setCouponCode('');
        setCheckout({});
        setCartItems([]);
        schedule?.onClear?.();
        setComponentToRender(false);
        setIsPix(false);
        setSearchParams((prevSearchParams) => {
            const newSearchParams = new URLSearchParams(prevSearchParams);
            newSearchParams.delete(routeParams.payments.checkout)

            return newSearchParams;
        });
        getOrder();
    }

    function navigateToLogin() {
        const currentParams = new URLSearchParams(location.search);
        currentParams.set('redirectTo', location.pathname);

        navigate({
            pathname: paths.login,
            search: currentParams.toString()
        });
    }

    const [showLoginModal, setShowLoginModal] = useState(false);

    return (
        <>
            {
                loading
                    ? (
                        <FullyCentered height='300px'>
                            <Loader />
                        </FullyCentered>
                    )
                    : (
                        <>
                            <Container>
                                <Head fullyCentralized>
                                    <ShoppingCart color={colors.occasionalPurple} size={20} />
                                    <Text
                                        type='bold'
                                        paddingBottom={0}
                                        marginLeft={'10px'}
                                        color={colors.occasionalPurple}
                                    >
                                        {schedule ? 'Seu carrinho' : 'Seu pedido'}
                                    </Text>
                                </Head>

                                <ContainerCode>
                                    <CouponButton>
                                        {
                                            isCouponModalVisible ? (
                                                <Button
                                                    width={'300px'}
                                                    variant='promoted'
                                                    category='squired'
                                                    size='small'
                                                    style={{
                                                        border: errorCode ? `2px solid ${colors.danger}` : `1px solid ${colors.success}`,
                                                        padding: `10px`,
                                                        background: couponCode ? '#f0fdf4' : 'none'
                                                    }}

                                                >
                                                    <Column>
                                                        <Row verticalCenter>
                                                            <Tag width={18} color={errorCode ? colors.danger : colors.successDark} />
                                                            <Text
                                                                color={colors.nightDark}
                                                                paddingBottom={0}
                                                                type='bold'
                                                                marginLeft={'8px'}
                                                            >
                                                                Código promocional
                                                            </Text>
                                                        </Row>
                                                        <Row>
                                                            <ContentFieldCode>
                                                                {
                                                                    couponCode ? (
                                                                        <CardCode>
                                                                            <ContentTextCode>
                                                                                <Check color={colors.successDark} size={'18px'} />
                                                                                <Text color={colors.successDark} marginLeft={'5px'} paddingBottom={'0px'}>
                                                                                    {couponCode}
                                                                                </Text>
                                                                                <CardTypeDiscount>
                                                                                    <Text color={colors.successDark}>
                                                                                        {typeDiscount} OFF
                                                                                    </Text>
                                                                                </CardTypeDiscount>
                                                                            </ContentTextCode>
                                                                            <X color={colors.nightDark} size={16} onClick={() => removeCode()} />
                                                                        </CardCode>

                                                                    ) : (
                                                                        <>
                                                                            <Fieldset
                                                                                placeholder='Digite seu código'
                                                                                layout='squired'
                                                                                onChangeValue={e => setPromotionalCode(e)}
                                                                            />

                                                                            {
                                                                                loading ?
                                                                                    <Loader /> :
                                                                                    <ButtonApplyCode onClick={() => getCoupon(promotionalCode, event.slug)}>{buttonText.apply}</ButtonApplyCode>
                                                                            }
                                                                        </>
                                                                    )
                                                                }

                                                            </ContentFieldCode>
                                                        </Row>
                                                        {
                                                            Boolean(errorCode) &&
                                                            <ContentErrorCode>
                                                                <Text color={colors.danger}>Código inválido!</Text>
                                                            </ContentErrorCode>
                                                        }

                                                    </Column>


                                                </Button>
                                            ) : (
                                                <Button
                                                    onClick={toggleCouponModal}
                                                    width={'300px'}
                                                    variant='promoted'
                                                    category='squired'
                                                    size='small'
                                                    style={{ border: `1px solid ${colors.success}` }}
                                                >
                                                    <Tag width={18} />
                                                    <Text
                                                        color={colors.success}
                                                        paddingBottom={0}
                                                        type='bold'
                                                        marginLeft={'8px'}
                                                    >
                                                        {buttonText.addCoupon}
                                                    </Text>
                                                </Button>

                                            )
                                        }



                                    </CouponButton>
                                </ContainerCode>

                                {schedule ? (
                                    <TicketsContainer>
                                        <Text
                                            type='bold'
                                            textAlign={'center'}
                                            marginTop={'6px'}
                                            color={colors.occasionalPurple}
                                        >
                                            Ingressos escolhidos
                                        </Text>

                                        {scheduleItems.length === 0 ? (
                                            <ScheduleCartEmpty>
                                                Escolha o ingresso do evento e marque presença nas sessões na
                                                programação. Cada ingresso vale para uma pessoa: 1 por tipo.
                                            </ScheduleCartEmpty>
                                        ) : (
                                            <Tickets>
                                                {scheduleItems.map(({ product, batch }) => {
                                                    const fee = serviceFee.perUnit(batch, serviceFee.METHODS.CREDIT_CARD, product.absorbed_service_fee);
                                                    const price = Number(batch.price);

                                                    return (
                                                        <ScheduleCartItem key={product.id}>
                                                            <Column style={{ minWidth: 0 }}>
                                                                <Text type='bold' marginBottom={'4px'} color={colors.nightDark}>
                                                                    {product.name}
                                                                </Text>
                                                                <Row verticalCenter>
                                                                    <Text type='bold' color={colors.night} paddingBottom={0}>
                                                                        {price > 0 ? currency.format(price) : 'Gratuito'}
                                                                    </Text>
                                                                    {fee > 0 && (
                                                                        <Text name='small' color={colors.night} marginLeft={'4px'} paddingBottom={0}>
                                                                            (+{fee.toFixed(2).replace('.', ',')} taxa)
                                                                        </Text>
                                                                    )}
                                                                </Row>
                                                            </Column>

                                                            <ScheduleCartRemove
                                                                type='button'
                                                                aria-label={`Remover ${product.name} do carrinho`}
                                                                onClick={() => schedule.onRemove(product.id)}
                                                            >
                                                                <X size={18} color={colors.night} />
                                                            </ScheduleCartRemove>
                                                        </ScheduleCartItem>
                                                    );
                                                })}
                                            </Tickets>
                                        )}

                                        {scheduleItems.length > 0 && schedule.presences?.length > 0 && (
                                            <Tickets>
                                                <Text type='bold' marginTop={'10px'} marginBottom={'2px'} color={colors.night}>
                                                    Presença nas sessões
                                                </Text>

                                                {schedule.presences.map(session => (
                                                    <ScheduleCartItem key={session.id}>
                                                        <Column style={{ minWidth: 0 }}>
                                                            <Text type='bold' marginBottom={'4px'} color={colors.nightDark}>
                                                                {session.title}
                                                            </Text>
                                                            <Text name='small' color={colors.night} paddingBottom={0}>
                                                                {formatSessionWhen(session)} · Gratuito
                                                            </Text>
                                                        </Column>

                                                        <ScheduleCartRemove
                                                            type='button'
                                                            aria-label={`Remover presença em ${session.title}`}
                                                            onClick={() => schedule.onRemovePresence(session.id)}
                                                        >
                                                            <X size={18} color={colors.night} />
                                                        </ScheduleCartRemove>
                                                    </ScheduleCartItem>
                                                ))}
                                            </Tickets>
                                        )}
                                    </TicketsContainer>
                                ) : (
                                <TicketsContainer
                                // isProducts={products.length > 0}
                                >

                                    {
                                        tickets.length > 0 && (
                                            <>
                                                <Text
                                                    type='bold'
                                                    textAlign={'center'}
                                                    marginTop={'6px'}
                                                    color={colors.occasionalPurple}
                                                >
                                                    Ingressos
                                                </Text>

                                                <Tickets>
                                                    {
                                                        tickets.map(el => {
                                                            // Item esgotado/encerrado/por abrir não tem nada a escolher:
                                                            // um único card com o nome do ingresso, em vez de repetir
                                                            // "Esgotado" uma vez por lote.
                                                            if (el?.soldout || el?.finished || el?.upcoming) {
                                                                return (
                                                                    <TicketItem
                                                                        minimal
                                                                        loginModalCount={loginModalCount}
                                                                        key={el.id}
                                                                        ticket={{
                                                                            id: el.id,
                                                                            name: el.name,
                                                                            description: el.description,
                                                                            price: el.price,
                                                                            original_price: el.price,
                                                                            quantity: 0,
                                                                            is_active: 0,
                                                                            sale_start: el?.next_sale_start,
                                                                        }}
                                                                        soldout={el?.soldout}
                                                                        finished={el?.finished}
                                                                        upcoming={el?.upcoming}
                                                                        type={el?.type}
                                                                        description={el?.description}
                                                                        saleType={el.sale_type}
                                                                        couponTrigger={couponTrigger}
                                                                        selectedTickets={cartItems}
                                                                        setSelectedTickets={setCartItems}
                                                                        onChangeTotalValue={handleUpdateTotal}
                                                                    />
                                                                );
                                                            }

                                                            if (el?.batches?.length > 0) {
                                                                return el.batches.map(item => (
                                                                    <TicketItem
                                                                        minimal
                                                                        loginModalCount={loginModalCount}
                                                                        key={item.id}
                                                                        ticket={item}
                                                                        soldout={el?.soldout}
                                                                        finished={el?.finished}
                                                                        type={el?.type}
                                                                        description={el?.description}
                                                                        saleType={el.sale_type}
                                                                        couponTrigger={couponTrigger}
                                                                        selectedTickets={cartItems}
                                                                        setSelectedTickets={setCartItems}
                                                                        onChangeTotalValue={handleUpdateTotal}
                                                                        absorbedServiceFee={el.absorbed_service_fee}
                                                                        requiresDocument={el.requires_document}
                                                                    />
                                                                ));
                                                            }

                                                            return null;
                                                        })
                                                    }
                                                </Tickets>
                                            </>
                                        )
                                    }
                                </TicketsContainer>
                                )}



                                <TicketsContainer isTypeProduct>
                                    {
                                        products.length > 0 && (
                                            <>
                                                <Text type='bold' textAlign={'center'} marginTop={0} color={colors.occasionalPurple}>
                                                    Produtos
                                                </Text>

                                                <Text textAlign={'center'} color={colors.night}>
                                                    Compre agora e retire no evento.
                                                </Text>
                                                <Tickets>
                                                    {
                                                        products.map((el, index) => (
                                                            <ProductItem
                                                                minimal
                                                                loginModalCount={loginModalCount}
                                                                key={index}
                                                                selectedProducts={cartItems}
                                                                setSelectedProducts={setCartItems}
                                                                couponTrigger={couponTrigger}
                                                                product={el}
                                                                onChangeTotalValue={handleUpdateTotal}
                                                                activeOverlayProductId={activeOverlayProductId}
                                                                onImageClick={(e) => handleOpenImage(e)}
                                                                onMouseEnter={() => setActiveOverlayProductId(el.id)}
                                                                onMouseLeave={() => setActiveOverlayProductId(null)}
                                                            />
                                                        ))
                                                    }
                                                </Tickets>
                                            </>
                                        )}
                                </TicketsContainer>

                                <Footer>
                                    {
                                        loading ? (
                                            <FullyCentered>
                                                <Loader />
                                            </FullyCentered>
                                        ) : (
                                            <>
                                                {
                                                    originalPriceTotal === totalPrice && (
                                                        <Column>
                                                            {serviceFeeTotal > 0 && (
                                                                <>
                                                                    <Row style={{ justifyContent: 'space-between' }}>
                                                                        <Text type='bold' color={colors.night} paddingBottom={0}>
                                                                            Subtotal
                                                                        </Text>
                                                                        <Text>
                                                                            {currency.format(totalPrice)}
                                                                        </Text>
                                                                    </Row>
                                                                    <Row style={{ justifyContent: 'space-between' }}>
                                                                        <Text type='bold' color={colors.night} paddingBottom={0}>
                                                                            Taxa de serviço
                                                                        </Text>
                                                                        <Text color={colors.night}>
                                                                            {currency.format(serviceFeeTotal)}
                                                                        </Text>
                                                                    </Row>
                                                                    {pixSavings > 0 && (
                                                                        <PixSavingsCallout>
                                                                            <Sparkles size={14} strokeWidth={2.5} color={colors.successDark} />
                                                                            <span>
                                                                                Pague no PIX e economize <strong>{currency.format(pixSavings)}</strong>
                                                                                {' '}— total de {currency.format(totalPrice + serviceFeePixTotal)}.
                                                                            </span>
                                                                        </PixSavingsCallout>
                                                                    )}
                                                                </>
                                                            )}
                                                            <Row style={{ justifyContent: 'space-between' }}>
                                                                <Text type='bold' color={colors.night} paddingBottom={0}>
                                                                    Total
                                                                </Text>
                                                                <Text name='highlight' color={colors.occasionalPurple}>
                                                                    {currency.format(totalPrice + serviceFeeTotal)}
                                                                </Text>
                                                            </Row>
                                                        </Column>
                                                    )
                                                }

                                                {
                                                    originalPriceTotal !== totalPrice &&
                                                    <Column>
                                                        <Row style={{ justifyContent: 'space-between' }}>
                                                            <Text type='bold' color={colors.night} paddingBottom={0}>
                                                                Subtotal
                                                            </Text>

                                                            <Text>
                                                                {currency.format(originalPriceTotal)}
                                                            </Text>
                                                        </Row>

                                                        <Row style={{ justifyContent: 'space-between' }}>
                                                            <Text type='bold' color={colors.night} paddingBottom={0}>
                                                                Desconto
                                                            </Text>

                                                            <Text color={colors.success}>
                                                                {currency.format((originalPriceTotal - totalPrice))}
                                                            </Text>
                                                        </Row>

                                                        {serviceFeeTotal > 0 && (
                                                            <>
                                                                <Row style={{ justifyContent: 'space-between' }}>
                                                                    <Text type='bold' color={colors.night} paddingBottom={0}>
                                                                        Taxa de serviço
                                                                    </Text>
                                                                    <Text color={colors.night}>
                                                                        {currency.format(serviceFeeTotal)}
                                                                    </Text>
                                                                </Row>
                                                                {pixFeeDiffers && (
                                                                    <PixFeeNote>
                                                                        No PIX a taxa é {currency.format(serviceFeePixTotal)} —
                                                                        total de {currency.format(totalPrice + serviceFeePixTotal)}.
                                                                    </PixFeeNote>
                                                                )}
                                                            </>
                                                        )}

                                                        <Row style={{ justifyContent: 'space-between' }}>
                                                            <Text type='bold' color={colors.night} paddingBottom={0}>
                                                                Total
                                                            </Text>

                                                            <Text name='highlight' color={colors.occasionalPurple}>
                                                                {currency.format(totalPrice + serviceFeeTotal)}
                                                            </Text>
                                                        </Row>
                                                    </Column>
                                                }

                                                {errorMinOrder && (
                                                    <FullyCentered>
                                                        <Text color={colors.danger} name='small'> {errorMinOrder}</Text>
                                                    </FullyCentered>
                                                )}

                                                <Button
                                                    size='medium'
                                                    category='squired'
                                                    variant={checkout?.batches?.length == 0 ? 'disabled' : 'featured'}
                                                    onClick={buy}
                                                >
                                                    Finalizar compra
                                                </Button>

                                                {hasServiceFee && (
                                                    <FullyCentered>
                                                        <ServiceFeeInfo />
                                                    </FullyCentered>
                                                )}


                                                {createOrderError && (
                                                    <FullyCentered>
                                                        <Text color={colors.danger} name='small'>{createOrderError}</Text>
                                                    </FullyCentered>
                                                )}
                                            </>
                                        )
                                    }
                                </Footer>
                            </Container>


                            <CheckoutCollapse
                                visible={searchParams.get(routeParams.payments.checkout) == 1 && !!componentToRender}
                                onClose={onCloseCollapse}
                            >
                                {!isPix ? componentToRender : pixToRender}
                            </CheckoutCollapse>
                            {/* {
                                componentToRender && (
                                )
                            } */}


                        </>
                    )

            }

            {
                goToLoginModal && (
                    <LoginModal
                        isOpen={goToLoginModal}
                        onClose={() => setGoToLoginModal(false)}
                    />
                )
            }

            <Modal isOpen={error} onClose={() => setError(false)}>
                <Column verticalCenter>
                    <Text color={colors.danger}>Você deve selecionar pelo menos 1 item!</Text>
                    <ContentButton>
                        <Button onClick={() => setError(false)} marginTop={'15px'}>
                            {buttonText.understood}
                        </Button>
                    </ContentButton>
                </Column>
            </Modal>

            {fullscreenImage && (
                <ModalOverlay onClick={handleCloseImage}>
                    <ModalContent>
                        <img src={fullscreenImage} alt="Produto" />
                    </ModalContent>
                </ModalOverlay>
            )}
        </>
    )
}

const StyledCheckoutOrder = styled(Column)`
    height: 100%;
`;

const Head = styled(Row)`
    background: #fff5fb;
    border-radius: 8px 8px 0 0;
    padding: 16px;
    width: 100%;
`;

const Container = styled.div`
    width: 100%;
    height: auto;
    /* padding-bottom: 300px; */
    /* background-color: #fefefe; */
    background-color: ${colors.neutralLight};    
    box-shadow: 0 0 4px #ccc;
    /* box-shadow: 0 2px 5px #ccc; */
    padding: 30px;
    border-radius: 8px;
    border: 1px solid ${colors.neutralDark};
    padding: 0 12px;
    position: sticky;
    top: 110px;    

    @media screen and (min-width: ${breakpoints.lg}) {
        width: 100%;
        padding: 0 !important;
    }
`;

const Footer = styled(FullyCentered)`
    flex-direction: column;
    width: 100%;
    padding: 10px;
    background: ${colors.neutralLight};
    /* box-shadow: 0 -2px 8px #ddd; */
    border-top: 1px solid ${colors.nightLight};
    padding-top: 10px;

    & > button {
        margin-top: 20px;
    }
    
    @media screen and (min-width: ${breakpoints.lg}) {
        padding-top: auto;
        padding-bottom: 10px;
        margin-bottom: 0;
        padding: 24px;

        /* & >  p {
            padding: 16px 0;
        } */

        & > button { 
            width: 100%;
            margin-bottom: 6px;
            box-shadow: unset;
            padding: 14px 10px;
        }
    }
`;

const CouponButton = styled(FullyCentered)`
    display: flex;
    flex-direction: column;
    margin-top: 10px;

    & > button {
        box-shadow: unset;
        padding: 6px;
    }
    
`;

const TicketsContainer = styled(FullyCentered)`
    flex-direction: column;
    width: 100%;
    padding-bottom: 20px;
`;

const ScheduleCartEmpty = styled.p`
    margin: 12px 0 0;
    text-align: center;
    font-size: 14px;
    line-height: 1.5;
    color: ${colors.night};
`;

const ScheduleCartItem = styled(Row)`
    margin-top: 14px;
    padding: 14px;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    border-radius: 6px;
    border: 1px solid ${colors.neutralDark};
    box-shadow: 0 0 3px #ccc;
`;

const ScheduleCartRemove = styled.button`
    flex: none;
    padding: 6px;
    border: 0;
    border-radius: 50%;
    background: none;
    cursor: pointer;

    &:hover { background: ${colors.neutralDark}; }
`;

const Tickets = styled(Column)`
    width: 100%;
    
    @media screen and (min-width: ${breakpoints.lg}) {
        width: 100%;
        background-color: ${colors.neutralLight};
        padding: 0 20px;
    }
`;


const ContentButton = styled(Row)`
    width: 270px;
`

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${indexPriority.threeDS + 1};
`;

const ModalContent = styled.div`
    max-width: 90%;
    max-height: 90%;

  @media screen and (min-width: ${breakpoints.lg}) {
    max-width: 45%;
    max-height: 45%;  
  }

  img {
    max-width: 100%;
    max-height: 100%;
    margin-top: -25%;
    border-radius: 8px;
  }
`;
const ContentFieldCode = styled(Row)`
    align-items: center;
    justify-content: flex-start;
    
    margin-top: 10px;
    & > div {
        
        padding-bottom: 0px;
        & > input {
            margin-top: 0px;
        }
    }
    
`
const ButtonApplyCode = styled.button`
    width: 100px;
    height: 37px;
    background: ${colors.occasionalPurple};
    border-radius: 6px;
    cursor: pointer;
    color: ${colors.neutralLight};
    transition: all ease-in-out .25s;
    margin-left: 5px;
`;

const ContainerCode = styled(Column)`
    width: 100%;
    @media screen and (min-width: ${breakpoints.lg}) {
        padding: 15px;
    }
`


const CardCode = styled.div`
    display: flex;
    align-items: center;
    padding: 0 10px;
    flex-direction: row;
    border: 1px solid #ccc;
    background: ${colors.neutralLight};
    border-radius: 6px;
    height: 37px;
    width: 100%;
    justify-content: space-between;
    position: relative;
   
`

const CardTypeDiscount = styled.div`
    width: auto;
    height: 20px;
    background: #f0fdf4;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
    padding: 5px;
    & > p {
        padding-bottom: 0;
        font-size: 11px;
    }
`

const ContentErrorCode = styled(Row)`
    justify-content: flex-start;
    margin-top: 10px;

    & > p {
        font-size: 12px;
        padding-bottom: 0px;
        
    }
`
const ContentTextCode = styled(Row)`
    justify-content: flex-start;
    & > p {
        display: flex;
        align-items: center;
    }
`

const PixFeeNote = styled.div`
    margin: 2px 0 8px;
    font-size: 12px;
    line-height: 1.4;
    color: ${colors.night};
`

const PixSavingsCallout = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 2px 0 8px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #EFFBF3;
    border: 1px solid ${colors.success};
    font-size: 12px;
    line-height: 1.4;
    color: ${colors.nightDark};

    & > svg {
        flex-shrink: 0;
        margin-top: 1px;
    }
`